<template>
    <section class="paneel filters" aria-label="Filters">
        <div class="filter-rij">
            <div class="veld f-datum">
                <label for="f-datum">Date range</label>
                <date-picker
                    id="f-datum"
                    v-model="rangeModel"
                    type="date"
                    range
                    value-type="date"
                    format="DD-MM-YYYY"
                    range-separator=" – "
                    :clearable="false"
                    :editable="false"
                    placeholder="Pick a date range"
                    aria-label="Date range"
                />
            </div>
            <div class="veld f-loc">
                <label for="f-loc">Location</label>
                <select id="f-loc" v-model="locationId">
                    <option v-for="l in sortedLocations" :key="l.id" :value="l.id">{{ l.id === "all" ? "All locations" : l.name }}</option>
                </select>
            </div>
            <div class="veld f-cube">
                <label for="f-cube">X-Cube</label>
                <select id="f-cube" v-model="xcubeId">
                    <option v-for="x in sortedXCubes" :key="x.id" :value="x.id">{{ x.id === "all" ? (locationId !== "all" ? "All cubes here" : "All X-Cubes") : x.display }}</option>
                </select>
            </div>
            <div class="veld f-exp">
                <label for="f-exp">Experience</label>
                <select id="f-exp" v-model="experienceId">
                    <option v-for="e in sortedExperiences" :key="e.id" :value="e.id">{{ e.id === "all" ? "All experiences" : e.name }}</option>
                </select>
            </div>
        </div>
        <div class="filter-voet">
            <div class="snel">
                <span class="label">Period</span>
                <button v-for="p in periods" :key="p.name" class="chip" type="button" :aria-pressed="fromStr === p.start && toStr === p.end" @click="applyPeriod(p)">{{ p.name }}</button>
            </div>
            <div class="rechts">
                <div class="opties">
                    <button class="btn btn-stil btn-sm" type="button" :aria-expanded="optionsOpen" aria-controls="f-opties-lijst" @click="optionsOpen = !optionsOpen">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h18M6 12h12M10 19h4" /></svg>
                        Options
                        <span v-if="bookingOptions.length" class="aantal">{{ bookingOptions.length }}</span>
                    </button>
                    <div v-show="optionsOpen" id="f-opties-lijst" class="uitklap">
                        <label v-for="o in OPTIONS" :key="o.value">
                            <input type="checkbox" :checked="bookingOptions.includes(o.value)" @change="toggleOption(o.value, $event.target.checked)" />
                            {{ o.label }}
                        </label>
                    </div>
                </div>
                <div class="veld" style="margin: 0">
                    <select v-model.number="limitValue" aria-label="Maximum number of bookings" style="padding-block: 8px; font-size: 13px">
                        <option v-for="n in LIMITS" :key="n" :value="n">Max {{ formatNumber(n) }}</option>
                    </select>
                </div>
                <button v-if="activeCount" class="btn btn-stil btn-sm" type="button" @click="clearFilters">Clear filters</button>
                <button class="btn btn-ghost btn-sm" type="button" :disabled="downloading" @click="downloadCSV">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
                    {{ downloading ? "Preparing…" : "Download CSV" }}
                </button>
            </div>
        </div>
    </section>
</template>

<script setup>
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import { computed, defineProps, ref } from "vue";
import { Message } from "element-ui";
import DatePicker from "vue2-datepicker";
import { useUrlFilters } from "../../composables/useUrlFilters";
import { saveTextAsFile } from "../../helpers/saveTextasFile";
import { getCustomersCSV } from "../../api/rest";

dayjs.extend(weekday);

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    experiences: {
        type: Array,
        default: () => [],
    },
});

const filterStore = useUrlFilters(
    () => props.locations,
    () => props.experiences,
);

const ALL_OPTION = { name: "All", id: "all", display: "All" };

// Real API option strings (sent in filters.options); labels mirror the prototype.
const OPTIONS = [
    { value: "Include deleted", label: "Include deleted" },
    { value: "Only deleted", label: "Only deleted" },
    { value: "Voucher", label: "Voucher" },
    { value: "Refund", label: "Refund" },
    { value: "Demo", label: "Demo" },
    { value: "Consent To Share", label: "Consent to share" },
    { value: "Not Finished", label: "Not finished" },
];
const LIMITS = [25, 50, 100, 250, 1000, 2500, 10000];

const optionsOpen = ref(false);
const downloading = ref(false);

const formatNumber = (n) => new Intl.NumberFormat("nl-NL").format(n);

const compareIds = (a, b) => (a === "all" || b === "all" ? a === b : a?.toString() === b?.toString());

// vue2-datepicker range: v-model is a [Date, Date] pair; the store keeps dayjs.
const rangeModel = computed({
    get: () => [filterStore.range.value[0].toDate(), filterStore.range.value[1].toDate()],
    set: (value) => {
        if (!value || !value[0] || !value[1]) {
            return;
        }
        filterStore.range.value = [dayjs(value[0]), dayjs(value[1])];
    },
});

// ISO strings only for marking the matching quick-period chip as active.
const fromStr = computed(() => filterStore.range.value[0].format("YYYY-MM-DD"));
const toStr = computed(() => filterStore.range.value[1].format("YYYY-MM-DD"));

const locationId = computed({
    get: () => filterStore.selectedLocation.value.id,
    set: (id) => {
        filterStore.selectedLocation.value = id === "all" ? ALL_OPTION : props.locations.find((l) => compareIds(l.id, id)) || ALL_OPTION;
        filterStore.selectedXCube.value = ALL_OPTION;
    },
});

const xcubeId = computed({
    get: () => filterStore.selectedXCube.value.id,
    set: (id) => {
        filterStore.selectedXCube.value = id === "all" ? ALL_OPTION : sortedXCubes.value.find((x) => compareIds(x.id, id)) || ALL_OPTION;
    },
});

const experienceId = computed({
    get: () => filterStore.selectedExperience.value.id,
    set: (id) => {
        filterStore.selectedExperience.value = id === "all" ? ALL_OPTION : props.experiences.find((e) => compareIds(e.id, id)) || ALL_OPTION;
    },
});

const limitValue = computed({
    get: () => filterStore.limit.value,
    set: (value) => {
        filterStore.limit.value = value;
    },
});

const bookingOptions = computed(() => filterStore.bookingOptions.value);

const toggleOption = (value, checked) => {
    let next = [...bookingOptions.value];
    if (checked) {
        next.push(value);
        // "Only deleted" and "Include deleted" are mutually exclusive.
        if (value === "Only deleted") {
            next = next.filter((o) => o !== "Include deleted");
        }
        if (value === "Include deleted") {
            next = next.filter((o) => o !== "Only deleted");
        }
    } else {
        next = next.filter((o) => o !== value);
    }
    filterStore.bookingOptions.value = next;
};

const sortedLocations = computed(() => [ALL_OPTION, ...props.locations]);

const sortedExperiences = computed(() => [ALL_OPTION].concat([...props.experiences].sort((a, b) => a.name.localeCompare(b.name))));

const sortedXCubes = computed(() => {
    let xcubes;
    if (filterStore.selectedLocation.value?.id !== ALL_OPTION.id) {
        xcubes = (filterStore.selectedLocation.value.xcubes || []).map((x) => ({ ...x, display: `${x.name} ${x.letter}` }));
    } else {
        xcubes = props.locations.reduce((acc, l) => acc.concat(l.xcubes.map((x) => ({ ...x, display: `${l.name} - ${x.name} ${x.letter}` }))), []);
    }
    const result = [...xcubes].sort((a, b) => a.name.localeCompare(b.name));
    return [ALL_OPTION, ...result];
});

const activeCount = computed(
    () =>
        (locationId.value !== "all" ? 1 : 0) +
        (xcubeId.value !== "all" ? 1 : 0) +
        (experienceId.value !== "all" ? 1 : 0) +
        bookingOptions.value.length,
);

const periods = computed(() => {
    const today = dayjs().startOf("day");
    const monday = today.weekday(1);
    const iso = (d) => d.format("YYYY-MM-DD");
    return [
        { name: "Today", start: iso(today), end: iso(today) },
        { name: "This week", start: iso(monday), end: iso(monday.add(6, "day")) },
        { name: "Last week", start: iso(monday.subtract(7, "day")), end: iso(monday.subtract(1, "day")) },
        { name: "This month", start: iso(today.startOf("month")), end: iso(today.endOf("month")) },
        { name: "Last month", start: iso(today.subtract(1, "month").startOf("month")), end: iso(today.subtract(1, "month").endOf("month")) },
        { name: "Last 30 days", start: iso(today.subtract(29, "day")), end: iso(today) },
    ];
});

const applyPeriod = (p) => {
    filterStore.range.value = [dayjs(p.start), dayjs(p.end)];
};

const clearFilters = () => {
    filterStore.selectedLocation.value = ALL_OPTION;
    filterStore.selectedXCube.value = ALL_OPTION;
    filterStore.selectedExperience.value = ALL_OPTION;
    filterStore.bookingOptions.value = [];
};

const downloadCSV = async () => {
    try {
        downloading.value = true;
        const filter = { ...filterStore.filters.value, limit: undefined };
        const result = await getCustomersCSV(filter);
        saveTextAsFile(result, "customer_data.csv", "csv");
    } catch (e) {
        Message.error({ message: `Could not export CSV: ${e.message}` });
    } finally {
        downloading.value = false;
    }
};
</script>
